// ═══ OPEN QUESTIONS — the rules the page and the import share (Philip 2026-10-29, docs/open-questions-plan.md) ═══
// Pure: no fetching. The page (src/pages/OpenQuestions.js) derives status and filters with these; the import
// (scripts/import-questions.mjs) maps a file's rows and matches their parcels with these.

export const TOPICS = ['Values', 'Appeals', 'Taxes', 'Payments', 'Status'];
export const STATUSES = [
  { key: 'open', label: 'Open' }, { key: 'claimed', label: 'Claimed' }, { key: 'answered', label: 'Answered' }, { key: 'closed', label: 'Closed' },
];

// Derived, never stored: closed → answered → claimed → open.
export const questionStatus = (q) => (q.closed_at ? 'closed' : q.answer != null ? 'answered' : q.claimed_by ? 'claimed' : 'open');

// Time-sensitive first, then by number.
export const sortQuestions = (qs) => [...qs].sort((a, b) =>
  (a.priority === 'time_sensitive' ? 0 : 1) - (b.priority === 'time_sensitive' ? 0 : 1) || a.number - b.number);

// ── The import ────────────────────────────────────────────────────────────────────────────────────────────────────
const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// The file's priority → the table's. Anything else is refused (null).
export function importPriority(p) {
  const k = norm(p);
  if (k === '' || k === 'normal') return 'normal';
  if (k === 'high' || k === 'timesensitive') return 'time_sensitive';
  return null;
}

// One parcel number in the file's words → the client's parcel, or null. Letters and digits only, against
// account_number and parcel_name; then a unique account number the text starts (≥ 6 characters: "30-338542"); then a
// unique account number the text ends with ("La Guardia Blk 1071 Lot 50"). More than one candidate = no match.
export function matchParcel(label, parcels) {
  const n = norm(String(label).replace(/\(fka[^)]*\)/i, ''));
  if (!n) return null;
  const tries = [
    (p) => norm(p.account_number) === n || norm(p.parcel_name) === n,
    (p) => n.length >= 6 && norm(p.account_number).startsWith(n),
    (p) => norm(p.account_number).length >= 6 && n.endsWith(norm(p.account_number)),
  ];
  for (const t of tries) {
    const hit = parcels.filter(t);
    if (hit.length === 1) return hit[0];
    if (hit.length > 1) return null;
  }
  return null;
}

// A file row → the import's row, or { errors }. `clients` / `properties` / `parcels`: the live rows (parcels carry
// property_id; properties carry client_id). The property is the matched parcels' one property when they share it
// (the file's label is then dropped — #14's "California (LA)" is San Jose Int'l Airport); with no parcel matched, the
// client's one property whose name contains the file's label; otherwise the label as text.
export function mapQuestion(row, { clients, properties, parcels }) {
  const errors = [];
  const client = clients.find((c) => norm(c.short_name) === norm(row.client) || norm(c.name) === norm(row.client));
  if (!client) errors.push(`client "${row.client}" isn't a client short name`);
  if (!TOPICS.includes(row.topic)) errors.push(`topic "${row.topic}" isn't one of ${TOPICS.join(', ')}`);
  const priority = importPriority(row.priority);
  if (!priority) errors.push(`priority "${row.priority}" isn't normal or high / time-sensitive`);
  if (!String(row.question ?? '').trim()) errors.push('no question');
  if (errors.length) return { errors };

  const props = properties.filter((p) => p.client_id === client.id);
  const propIds = new Set(props.map((p) => p.id));
  const mine = parcels.filter((p) => propIds.has(p.property_id));
  const labels = String(row.parcel ?? '').split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean);
  const matched = labels.map((label) => ({ label, parcel: matchParcel(label, mine) }));
  const onProps = [...new Set(matched.filter((m) => m.parcel).map((m) => m.parcel.property_id))];
  let property = onProps.length === 1 ? props.find((p) => p.id === onProps[0]) : null;
  if (!property && !onProps.length && norm(row.property)) {
    const named = props.filter((p) => norm(p.name).includes(norm(row.property)));
    if (named.length === 1) property = named[0];
  }
  return {
    errors: [],
    client,
    property,
    matched,
    labelDiffers: !!property && !norm(property.name).includes(norm(row.property)),
    insert: {
      client_id: client.id,
      property_id: property?.id ?? null,
      property_text: property ? null : (String(row.property ?? '').trim() || null),
      years: String(row.years ?? '').trim() || null,
      topic: row.topic,
      priority,
      question: String(row.question).trim(),
      wigley_now: String(row.wigley ?? '').trim() || null,
      background: String(row.context ?? '').trim() || null,
      parcels: matched.map((m) => ({ label: m.label, parcel_id: m.parcel?.id ?? null })),
    },
  };
}
